<script setup lang="ts">
  import { ref, onMounted, watch, computed } from 'vue';
  import { SearchCombobox, FilterModal, ThemeProvider } from '@/shared/ui';
  import {
    useDepartmentOptions,
    useFunctionalActivityOptions,
    usePositionOptions,
  } from '../../lib';
  import type { MembersFilter } from '@/entities/Branch';

  const props = defineProps<{
    currentFilters?: MembersFilter;
  }>();

  const emit = defineEmits<{
    close: [];
    'update:filter': [filters: MembersFilter];
    'clear-filters': [];
  }>();

  const initialDeptId = computed(() => props.currentFilters?.departmentId);

  const {
    departmentOptions,
    isLoading: isDepartmentLoading,
    loadDepartments,
    handleSearch: handleDepartmentSearch,
    initialDepartmentLabel,
  } = useDepartmentOptions({ initialDepartmentId: initialDeptId });

  const {
    positions,
    isLoading: isPositionLoading,
    loadPositions,
  } = usePositionOptions();

  const {
    functionalActivities,
    isLoading: isFunctionalActivityLoading,
    loadFunctionalActivities,
  } = useFunctionalActivityOptions();

  const positionSearchQuery = ref('');
  const functionalActivitySearchQuery = ref('');

  const positionComboboxOptions = computed(() => {
    const q = positionSearchQuery.value.trim().toLowerCase();
    return positions.value
      .filter((p) => !q || p.name.toLowerCase().includes(q))
      .map((p) => ({ value: String(p.id), label: p.name }));
  });

  const functionalActivityComboboxOptions = computed(() => {
    const q = functionalActivitySearchQuery.value.trim().toLowerCase();
    return functionalActivities.value
      .filter((a) => !q || a.name.toLowerCase().includes(q))
      .map((a) => ({ value: String(a.id), label: a.name }));
  });

  const formData = ref({
    positionId: '',
    departmentId: '',
    functionalActivityId: '',
  });

  const initialPositionLabel = computed(() => {
    const id = formData.value.positionId;
    if (!id) return undefined;
    const p = positions.value.find((x) => String(x.id) === id);
    return p?.name;
  });

  const initialFunctionalActivityLabel = computed(() => {
    const id = formData.value.functionalActivityId;
    if (!id) return undefined;
    const a = functionalActivities.value.find((x) => String(x.id) === id);
    return a?.name;
  });

  function loadFilters(filters?: MembersFilter) {
    if (!filters) return;
    formData.value.positionId = filters.positionId ? String(filters.positionId) : '';
    formData.value.departmentId = filters.departmentId ? String(filters.departmentId) : '';
    formData.value.functionalActivityId = filters.functionalActivityId
      ? String(filters.functionalActivityId)
      : '';
  }

  onMounted(async () => {
    await Promise.all([loadDepartments(), loadFunctionalActivities(), loadPositions()]);
    loadFilters(props.currentFilters);
  });

  watch(
    () => props.currentFilters,
    (newFilters) => {
      if (newFilters) loadFilters(newFilters);
    },
    { deep: true }
  );

  const handleApplyFilters = () => {
    const filters: MembersFilter = {};
    if (formData.value.positionId) filters.positionId = Number(formData.value.positionId);
    if (formData.value.departmentId) filters.departmentId = Number(formData.value.departmentId);
    if (formData.value.functionalActivityId)
      filters.functionalActivityId = Number(formData.value.functionalActivityId);
    emit('update:filter', filters);
  };

  const handleClearFilters = () => {
    formData.value.positionId = '';
    formData.value.departmentId = '';
    formData.value.functionalActivityId = '';
    positionSearchQuery.value = '';
    functionalActivitySearchQuery.value = '';
    emit('update:filter', {});
  };

  const handlePositionSearch = (value: string) => {
    positionSearchQuery.value = value;
  };

  const handleFunctionalActivitySearch = (value: string) => {
    functionalActivitySearchQuery.value = value;
  };
</script>

<template>
  <FilterModal
    content-class="w-auto max-w-md"
    :buttons-text="{ clear: 'Limpar filtro', apply: 'Aplicar filtro' }"
    @close="emit('close')"
    @apply-filters="handleApplyFilters"
    @clear-filters="handleClearFilters"
  >
    <template #content>
      <ThemeProvider>
        <div class="p-3">
          <div class="p-6 space-y-6">
            <div>
              <h4 class="font-medium text-gray-07 pb-4">
                Cargos:
              </h4>
              <SearchCombobox
                :model-value="formData.positionId"
                :options="positionComboboxOptions"
                :loading="isPositionLoading"
                :initial-label="initialPositionLabel"
                trigger-icon="search"
                placeholder="Selecione um cargo"
                search-placeholder="Buscar cargo..."
                class="w-full min-w-70"
                @update:model-value="formData.positionId = $event || ''"
                @search="handlePositionSearch"
              />
            </div>
            <div>
              <h4 class="font-medium text-gray-07 pb-4">
                Departamentos:
              </h4>
              <SearchCombobox
                :model-value="formData.departmentId"
                :options="departmentOptions"
                :loading="isDepartmentLoading"
                :initial-label="initialDepartmentLabel"
                trigger-icon="search"
                placeholder="Selecione um departamento"
                search-placeholder="Buscar departamento..."
                class="w-full min-w-70"
                @update:model-value="formData.departmentId = $event || ''"
                @search="handleDepartmentSearch"
              />
            </div>
            <div>
              <h4 class="font-medium text-gray-07 pb-4">
                Atividade Funcional:
              </h4>
              <SearchCombobox
                :model-value="formData.functionalActivityId"
                :options="functionalActivityComboboxOptions"
                :loading="isFunctionalActivityLoading"
                :initial-label="initialFunctionalActivityLabel"
                trigger-icon="search"
                placeholder="Selecione uma atividade funcional"
                search-placeholder="Buscar atividade funcional..."
                class="w-full min-w-70"
                @update:model-value="formData.functionalActivityId = $event || ''"
                @search="handleFunctionalActivitySearch"
              />
            </div>
          </div>
        </div>
      </ThemeProvider>
    </template>
  </FilterModal>
</template>
